import { useId, useMemo, useState } from "react";
import { LuSearch } from "@platform/icon";
import type { EducationMemberOption } from "@platform/billing";
import {
  memberSessionState,
  type MemberSessionState,
} from "../lib/education-member";

interface EducationMemberPickerProps {
  members: EducationMemberOption[];
  selectedUserIds: string[];
  /** 이 목록이 속한 회차. 이미 접수된 사람을 잠그는 기준이다. */
  sessionId: string;
  disabled?: boolean;
  onToggle: (member: EducationMemberOption, checked: boolean) => void;
}

const MEMBER_SCROLL_THRESHOLD = 5;
const MEMBER_SEARCH_THRESHOLD = 10;

/**
 * 기업 교육 접수 전용 구성원 선택.
 *
 * 셀렉트가 아니라 체크박스 목록인 이유: 한 회차에 여러 명을 넣는 게 기본이라
 * 셀렉트로는 "열고 → 고르고 → 닫히고"를 인원 수만큼 반복해야 한다. 목록이면
 * 누가 이미 들어갔는지도 같은 화면에서 보인다.
 */
export function EducationMemberPicker({
  members,
  selectedUserIds,
  sessionId,
  disabled = false,
  onToggle,
}: EducationMemberPickerProps) {
  const [keyword, setKeyword] = useState("");
  const searchId = useId();
  const showSearch = members.length >= MEMBER_SEARCH_THRESHOLD;
  const useScrollableList = members.length >= MEMBER_SCROLL_THRESHOLD;
  const selected = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  // 이미 이 회차에 들어가 있는 사람(결제 완료 + 결제 대기). 서버가 접수 때 막는 것과
  // 같은 기준이라, 여기서 잠가 두면 제출하고 나서야 거부당하는 왕복이 없어진다.
  const takenState = useMemo(() => {
    const map = new Map<string, MemberSessionState>();
    for (const member of members) {
      const state = memberSessionState(member, sessionId);
      if (state) map.set(member.userId, state);
    }
    return map;
  }, [members, sessionId]);

  const filtered = useMemo(() => {
    const needle = showSearch ? keyword.trim().toLowerCase() : "";
    if (!needle) return members;
    return members.filter((member) =>
      `${member.name} ${member.email} ${member.phone}`
        .toLowerCase()
        .includes(needle),
    );
  }, [keyword, members, showSearch]);

  // 검색으로 걸러낸 목록에만 적용한다 — 보이지 않는 사람이 딸려 들어가면 안 된다.
  // 이미 접수된 사람은 애초에 고를 수 없으므로 '전체' 의 범위에서도 뺀다.
  const selectable = useMemo(
    () => filtered.filter((member) => !takenState.has(member.userId)),
    [takenState, filtered],
  );

  const allFilteredSelected =
    selectable.length > 0 &&
    selectable.every((member) => selected.has(member.userId));

  const toggleAllFiltered = () =>
    selectable.forEach((member) => {
      if (selected.has(member.userId) === allFilteredSelected)
        onToggle(member, !allFilteredSelected);
    });

  if (members.length === 0)
    return (
      <p className="mt-4 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:bg-white/[0.03] dark:text-neutral-400">
        선택할 수 있는 구성원이 없습니다.
      </p>
    );

  return (
    <div className="mt-4 overflow-hidden rounded-md border border-gray-200 dark:border-white/10">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
        {showSearch ? (
          <>
            <label htmlFor={searchId} className="sr-only">
              구성원 검색
            </label>
            <div className="relative min-w-0 flex-1">
              <LuSearch
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
              />
              <input
                id={searchId}
                type="search"
                value={keyword}
                disabled={disabled}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="이름 · 이메일 검색"
                className="h-9 w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-900 outline-none transition-colors hover:border-gray-400 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/10 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-white/15 dark:bg-white/5 dark:text-neutral-100"
              />
            </div>
          </>
        ) : (
          <span className="min-w-0 flex-1 text-xs text-gray-500 dark:text-neutral-400">
            참여할 구성원을 체크해 주세요.
          </span>
        )}
        <button
          type="button"
          disabled={disabled || selectable.length === 0}
          onClick={toggleAllFiltered}
          className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-brand-700 underline underline-offset-4 hover:text-brand-800 disabled:cursor-not-allowed disabled:text-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-300 dark:hover:text-brand-200"
        >
          {allFilteredSelected ? "전체 해제" : "전체 선택"}
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-neutral-400">
          검색 결과가 없습니다.
        </p>
      ) : (
        <ul
          className={`divide-y divide-gray-100 dark:divide-white/10 ${
            useScrollableList ? "max-h-56 overflow-y-auto" : ""
          }`}
        >
          {filtered.map((member) => {
            const taken = takenState.get(member.userId);
            const registered = !!taken;
            const checked = selected.has(member.userId);
            return (
              <li key={member.userId}>
                <label
                  className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
                    registered
                      ? "cursor-not-allowed bg-gray-50/80 dark:bg-white/[0.02]"
                      : checked
                        ? "cursor-pointer bg-brand-50/70 dark:bg-brand-500/10"
                        : "cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled || registered}
                    onChange={(event) => onToggle(member, event.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-600/30 disabled:cursor-not-allowed dark:border-white/20 dark:bg-white/5"
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`flex min-w-0 items-center gap-1.5 text-sm font-semibold ${
                        registered
                          ? "text-gray-400 dark:text-neutral-500"
                          : "text-gray-900 dark:text-neutral-100"
                      }`}
                    >
                      <span className="truncate">{member.name}</span>
                      {taken === "registered" && (
                        <span className="shrink-0 rounded-sm bg-gray-200 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-white/10 dark:text-neutral-300">
                          신청 완료
                        </span>
                      )}
                      {/* 결제창을 닫은 접수 — 명부엔 올라가 있지만 확정 전이다. 내 접수 내역에서 이어서 결제한다. */}
                      {taken === "pending" && (
                        <span className="shrink-0 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                          결제 대기
                        </span>
                      )}
                    </span>
                    <span
                      className={`mt-0.5 block truncate text-xs ${
                        registered
                          ? "text-gray-400 dark:text-neutral-500"
                          : "text-gray-500 dark:text-neutral-400"
                      }`}
                    >
                      {member.email}
                      {member.phone ? ` · ${member.phone}` : ""}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
